import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Mail, Hash, Lock } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { setAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ManageBookingPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ email: user?.email || "", booking_ref: "", pin: "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/bookings/lookup-by-ref", {
        email: form.email.trim(),
        booking_ref: form.booking_ref.trim().toUpperCase(),
        pin: form.pin.trim(),
      });
      // Store the self-service token and redirect to the booking management page
      sessionStorage.setItem("berdhill_self_service_token", r.data.token);
      sessionStorage.setItem("berdhill_self_service_booking", JSON.stringify(r.data.booking));
      toast.success("Booking found");
      nav("/manage/" + r.data.booking.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No booking matches those details");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SiteLayout>
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-secondary/50 text-brand-primary mb-4">
            <KeyRound size={22} />
          </div>
          <h1 className="font-display text-4xl">Manage your booking</h1>
          <p className="text-brand-muted text-sm mt-2">
            Use the booking reference and PIN we sent to your email — no account needed.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-brand-border rounded-md p-8 space-y-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-brand-muted flex items-center gap-1"><Mail size={11} /> Email</span>
            <input
              data-testid="manage-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2.5 text-sm"
              placeholder="you@example.com"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-brand-muted flex items-center gap-1"><Hash size={11} /> Booking reference</span>
            <input
              data-testid="manage-ref"
              required
              value={form.booking_ref}
              onChange={(e) => setForm({ ...form, booking_ref: e.target.value.toUpperCase() })}
              className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2.5 text-base font-mono tracking-wider uppercase"
              placeholder="BR-XXXXX"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-brand-muted flex items-center gap-1"><Lock size={11} /> Access PIN</span>
            <input
              data-testid="manage-pin"
              required
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
              className="mt-1 w-full border border-brand-border rounded-sm px-3 py-2.5 text-2xl tracking-[0.4em] font-mono text-center"
              placeholder="••••••"
            />
          </label>

          <button
            data-testid="manage-submit"
            disabled={busy}
            type="submit"
            className="w-full bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-50 text-white py-3 rounded-sm text-sm font-medium"
          >
            {busy ? "Looking up…" : "Find my booking"}
          </button>

          <p className="text-[11px] text-center text-brand-muted">
            Lost your PIN? Email <a href="mailto:berdhillrooms@gmail.com" className="text-brand-primary underline">berdhillrooms@gmail.com</a> from the same address used at booking.
          </p>
        </form>
      </div>
    </SiteLayout>
  );
}
